import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipEvent, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput, MatInputModule } from '@angular/material/input';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FilterChip } from '../../models/filter-chip';
import { BehaviorSubject, combineLatest, debounce, debounceTime, firstValueFrom, Observable, of, Subject } from 'rxjs';

export enum Modifiers {
  None = 0,
  Negatable = 1 << 0,
  MultiSelect = 1 << 1 // Not yet implemented
  // Future...
}
@Component({
  selector: 'app-filter-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatInputModule
  ],
  templateUrl: './filter-editor.component.html',
  styleUrls: ['./filter-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterEditorComponent),
      multi: true
    }
  ]
})
export class FilterEditorComponent implements ControlValueAccessor {
  @Input() label: string = 'Filter';
  @Input() hint: string = '';
  @Input() chips: string[] = []; // List of possible chip types
  @Input() chipPossVals: { [key: string]: string[] | ((partial: string) => Promise<string[] | string[][]>) } = {
    status: ["Submitted", "Unsubmitted", "Review", "Approved"]
  }
  @Input() chipModifiers: { [key: string]: Modifiers } = { "status": Modifiers.Negatable };
  /** Help text for each chip. */
  @Input() chipHelpers: { [key: string]: string} = {};
  @Input() flags: string[] = [];
  @Output() filterChange = new EventEmitter<FilterChip[]>();
  @ViewChild(MatInput) inputElement!: MatInput;
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;
  readonly debouncer: Subject<FilterChip[]> = new Subject<FilterChip[]>();

  filterControl = new FormControl('');
  @Input() selectedFilters: FilterChip[] = [];
  activeChip: FilterChip | null = null; // Tracks chip being edited
  preventChipCreate = false;

  onChange: (value: FilterChip[]) => void = () => { };
  onTouched: () => void = () => { };
  chipsAndFlags?: string[];
  availableChips: Subject<string[]> = new BehaviorSubject(<string[]>[]);
  availableChips$ = this.availableChips.pipe(debounceTime(100));

  private getChipPrefix(chip: string, isNegated: boolean): string {
    return isNegated ? `not ${chip}:` : `${chip}:`;
  }

  constructor() {
    this.debouncer.pipe(debounceTime(100)).subscribe(v => this.filterChange.emit(this.selectedFilters))
    combineLatest([this.filterControl.valueChanges.pipe(debounceTime(100)), this.filterChange]).subscribe(async ([v, _]) => {
      v = this.inputElement.value.trim();
      if (!this.activeChip) {
        const available: string[] = [];
        for (const chip of this.chips) {
          // Only show if neither negated nor non-negated version is already selected
          const isChipAlreadySelected = this.selectedFilters.some(f => f.chip === chip);
          if (!isChipAlreadySelected && (!v?.trim() || chip.toLowerCase().startsWith(v.toLowerCase()))) {
            available.push(chip);
          }
          if (this.chipModifiers[chip] & Modifiers.Negatable) {
            const notChip = `not ${chip}`;
            // Only show negated version if neither version is already selected
            if (!isChipAlreadySelected && (!v?.trim() || notChip.toLowerCase().startsWith(v.toLowerCase()))) {
              available.push(notChip);
            }
          }
        }
        for (const flag of this.flags) {
          if (!this.selectedFilters.some(f => f.chip === flag && f.isFlag) && (!v?.trim() || flag.toLowerCase().startsWith(v.toLowerCase()))) {
            available.push(flag);
          }
        }
        this.availableChips.next(available);
        return;
      }
      const chipVal = this.inputElement?.value.substring(this.getChipPrefix(this.activeChip.chip!, this.activeChip.isNegated!).length);
      const possVals = this.chipPossVals[this.activeChip.chip!];
      // console.log("filtering", this.activeChip, chipVal);    
      if (possVals instanceof Function) {
        const fn: Function = <any>this.chipPossVals[this.activeChip.chip!];
        const results = await fn(chipVal);
        console.log("Results from chip", results);
        this.availableChips.next(results);
      } else if (possVals) {
        const arr: string[] = <any>this.chipPossVals[this.activeChip.chip!];
        this.availableChips.next(arr.filter(f => f.toLowerCase().includes(chipVal.toLowerCase())));
        console.log("pv chips", possVals);
      } else {
        this.availableChips.next([]);
      }
    })
  }

  ngOnInit() {
    this.chipsAndFlags = this.chips.concat(this.flags);
  }

  ngOnChanges() {
    this.debouncer.next(this.selectedFilters);// Initial emit, if there are any...
  }

  toggleNegation(filter: FilterChip) {
    if ((this.chipModifiers[filter.chip ?? '*'] & Modifiers.Negatable) != 1) return;
    filter.isNegated = !filter.isNegated;
    this.updateValue();
  }

  /**
   * Handles input changes:
   * - If input matches a chip name + `:`, it starts editing that chip.
   * - Otherwise, it acts as a normal free-text filter.
   */
  onInputChange(value: string): void {
    // console.info(this.onInputChange.name, value, this.activeChip);
    value = value.trim();

    // If input matches "chipName:" or "not chipName:", enter chip-edit mode
    if (!this.activeChip) {
      for (const chip of this.chips) {
        if (value.toLowerCase() === `${chip.toLowerCase()}:`) {
          this.activeChip = { chip, value: '', isNegated: false };
          this.filterControl.setValue('');
          return;
        } else if (value.toLowerCase() === `not ${chip.toLowerCase()}:`) {
          this.activeChip = { chip, value: '', isNegated: true };
          this.filterControl.setValue('');
          return;
        }
      }
    }

    if (this.activeChip) {
      const prefix = this.getChipPrefix(this.activeChip.chip!, this.activeChip.isNegated!);
      if (!value.startsWith(prefix)) {
        this.activeChip = null;
      } else {
        this.activeChip.value = value.substring(prefix.length);
      }
    }
  }

  /**
   * Handles Enter key when inputting free text filters.
   */
  async addFreeTextFilter(event: MatChipInputEvent) {
    console.info(this.addFreeTextFilter.name);
    const availableChips = await firstValueFrom(this.availableChips$);
    console.info("Await completed");
    const rawValue = this.inputElement.value.trim();
    console.log("Free text state", rawValue, this.autocomplete.activeOption?.value)
    if (!rawValue) {
      console.info("2nd enter", rawValue, event);
      this.autocomplete.closePanel(); // Assume the user is trying to clear the autocomplete with a second "enter" press.
      return; // Ignore empty values
    }

    if (this.activeChip?.chip && availableChips.includes(this.activeChip.chip)) {
      console.info("Skipping already added");
      return;
    }
    else if (this.activeChip?.chip && this.autocomplete.activeOption) {
      this.selectedFilters.push({ ...this.activeChip, value: this.autocomplete.activeOption.value });
      this.activeChip = null; // Exit chip-edit mode
      this.preventChipCreate = true;
    } else if (this.activeChip && this.activeChip.chip) {
      // Finalize the chip-value pair
      let packedValue;
      if (this.chipPossVals[this.activeChip.chip] && this.autocomplete.activeOption?.value || availableChips.length == 1) {
        packedValue = this.autocomplete.activeOption?.value ?? availableChips[0];
      } else {
        packedValue = rawValue.substring(this.activeChip.chip.length + 1)
      }
      const value = packedValue instanceof Array ? packedValue[0] : packedValue;
      this.selectedFilters.push({ ...this.activeChip, value });
      this.activeChip = null; // Exit chip-edit mode
    } else {
      // Add a free-text filter
      const value = rawValue;
      this.selectedFilters.push({ chip: null, value });
    }
    console.info("Final state", this.activeChip, this.selectedFilters);
    // Clear input field after adding a filter
    this.filterControl.setValue('');
    event.chipInput!.clear();
    this.updateValue();
  }

  /**
   * Removes a selected filter chip.
   */
  removeFilter(filter: FilterChip): void {
    this.selectedFilters = this.selectedFilters.filter(f => f !== filter);
    this.updateValue();
  }

  handleEnterKey(event: Event) {
    console.debug(this.handleEnterKey.name, this.autocomplete.activeOption);
    if (!this.autocomplete.activeOption) return;
    const packedValue = this.autocomplete.activeOption.value;
    const value = packedValue instanceof Array ? packedValue[0] : packedValue;
    if (this.activeChip?.chip && this.chipPossVals[this.activeChip.chip]) {
      this.activeChip.value = value
      console.log("Completing autocomplete chip", this.activeChip);
      event.preventDefault();
    } else {
      console.debug("Manually emitting", value)
      // Manually emit optionSelected behavior
      this.selectChip({ option: { value } } as MatAutocompleteSelectedEvent);
      event.preventDefault(); // Prevents chip input from handling Enter
    }
  }

  /**
   * Handles chip selection from autocomplete.
   * - Moves the input into chip-edit mode.
   */
  selectChip(event: MatAutocompleteSelectedEvent): void {
    console.info(this.selectChip.name, event.option.value);
    let selectedChip = <string>event.option.value;
    if (!selectedChip) return;
    if (this.preventChipCreate) {
      this.preventChipCreate = false;
      return;
    }

    let isNegated = false;
    if (selectedChip.startsWith('not ')) {
      selectedChip = selectedChip.substring(4);
      isNegated = true;
    }

    if (this.activeChip?.chip && this.chipPossVals[this.activeChip.chip]) {
      console.log("Handling poss value click", event.option.value)
      this.activeChip.value = event.option.value;
      this.selectedFilters.push(this.activeChip);
      this.activeChip = null;
      this.inputElement.value = '';
      this.updateValue();
    } else if (!this.flags.find(f => f === selectedChip)) {
      // Enter chip-edit mode and show "chip:" in input field
      this.inputElement.value = this.getChipPrefix(selectedChip, isNegated);
      this.activeChip = { chip: selectedChip, value: '', isNegated };
      console.log("selecting ", selectedChip);
      if (this.chipPossVals[selectedChip])
        setTimeout(() => {
          console.log("re-opening panel");
          this.autocomplete.openPanel();
        }, 50);
    } else { // Flag
      this.selectedFilters.push({ chip: selectedChip, isFlag: true, value: '1' });
      this.updateValue();
    }

    // Move focus back to the input field for immediate editing
    //this.inputElement.focus();
  }

  private updateValue() {
    this.onChange(this.selectedFilters);
    this.debouncer.next(this.selectedFilters);
  }

  // **ControlValueAccessor Methods**
  writeValue(value: FilterChip[]): void {
    this.selectedFilters = value || [];
  }

  registerOnChange(fn: (value: FilterChip[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.filterControl.disable();
    } else {
      this.filterControl.enable();
    }
  }

  clear() {
    this.inputElement.value = '';
    this.selectedFilters = [];
    this.activeChip = null;
    this.updateValue();
    console.log("setting it to null")
  }
}
